<template>
  <div class="mask" @click.self="onClickClose">
    <div class="dialog" role="dialog" aria-modal="true" :aria-labelledby="titleId" @keydown="onKeydown">
      <header class="dialog__header">
        <h2 :id="titleId" class="dialog__title">{{ label }}</h2>
      </header>

      <div class="dialog__body">
        <p class="dialog__description">{{ description }}</p>
      </div>

      <footer class="dialog__footer">
        <button ref="closeButtonRef" tabindex="0" type="button" class="dialog__close-button" @click="onClickClose">
          {{ $t("common.close") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

/**
 * 校正ルールの説明ダイアログ。
 *
 * タイトルに `label`、本文に `description` を表示し、
 * 灰色の「閉じる」ボタンのみを持つ。
 *
 * Vue Modal Dialogs (`vue-modal-dialogs`) で `this.$close()` を呼ぶ前提。
 */
export default Vue.extend({
  props: {
    label: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      titleId: "proofreading-rule-description-dialog-title",
    };
  },
  mounted() {
    window.addEventListener("popstate", this.onPopState);
    (this.$refs.closeButtonRef as HTMLButtonElement)?.focus();
  },
  beforeDestroy() {
    window.removeEventListener("popstate", this.onPopState);
  },
  methods: {
    onPopState() {
      this.$close();
    },
    onClickClose() {
      this.$close();
    },
    onKeydown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        this.onClickClose();
      }
    },
  },
});

export type ProofreadingRuleDescriptionDialogOptions = {
  label: string;
  description: string;
};
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9100;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(400px, 92vw);
  max-height: 86vh;
  margin: 0;
  border: none;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
  overflow: hidden;
}

.dialog__header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 20px;
}

.dialog__title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  text-align: center;
  line-height: var(--line-height-default);
}

.dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
}

.dialog__description {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  color: var(--color-text-normal);
  line-height: var(--line-height-default);
  white-space: pre-wrap;
}

.dialog__footer {
  display: flex;
  justify-content: center;
  padding: 16px 24px 24px;
}

.dialog__close-button {
  position: relative;
  min-width: 120px;
  width: 100%;
  padding: 10px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;
}
</style>
